import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  BenefitDetailWrapper,
  BenefitFormWrapper,
  BenefitSaveBtn,
  ChooseTypeWrapper,
} from "./styles";
import { BaseDropdown, LargeButton, SuccessModal } from "components/core";
import BenefitSetupDetailSection from "./BenefitSetupDetailSection";
import DependentsCard from "pages/GradeSetup/DependentsCard";
import { Field, Form, Formik } from "formik";
import useFetchWellness from "./useFetchWellness";
import BenefitSetupDetailGrade from "./BenefitSetupDetailGrade";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import http from "services/httpService";
import { Ptag } from "components/core/fonts";
import { Box, CircularProgress } from "@mui/material";
import * as Yup from "yup";

const CHOICE_TYPE = [
  { display_name: "Same for All Employees", value: "SAME" },
  { display_name: "Graded as per GMC", value: "GRADED" },
];

const INIT_FORMIK_VALUES = {
  choiceType: "SAME",
  entity_id: "",
  benefit_details: [""],
  vendor_id: "",
  benefit_heading: "",
  benefit_description: "",
  redeem_option_data: "",
  dependents_covered: [],
  grades: [],
};

const getEmptyFormValues = (choiceType = "SAME") => ({
  ...INIT_FORMIK_VALUES,
  choiceType,
});

// Validation schema for required fields
const validationSchema = Yup.object().shape({
  vendor_id: Yup.string().required("*Required"),
  entity_id: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  benefit_heading: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  benefit_description: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  dependents_covered: Yup.array().when('choiceType', {
    is: 'SAME',
    then: Yup.array().min(1, "*Required"),
    otherwise: Yup.array().nullable()
  }),
  grades: Yup.array().when('choiceType', {
    is: 'GRADED',
    then: Yup.array().min(1, "*Required").of(
      Yup.object().shape({
        entity_id: Yup.string().required("*Required"),
        benefit_heading: Yup.string().required("*Required"),
        benefit_description: Yup.string().required("*Required"),
        dependents_covered: Yup.array().min(1, "*Required"),
      })
    ),
    otherwise: Yup.array().nullable()
  })
});

// Function to check if form has valid data
const isFormValid = (values) => {
  if (!values) return false;
  
  // Check if vendor is selected
  if (!values.vendor_id) return false;
  
  // For SAME mode, check main form fields
  if (values.choiceType === 'SAME') {
    return values.entity_id && 
           values.benefit_heading && 
           values.benefit_description && 
           values.dependents_covered && 
           values.dependents_covered.length > 0;
  }
  
  // For GRADED mode, check if grades have valid data
  if (values.choiceType === 'GRADED') {
    return values.grades && 
           values.grades.length > 0 && 
           values.grades.every(grade => 
             grade.entity_id && 
             grade.benefit_heading && 
             grade.benefit_description && 
             grade.dependents_covered && 
             grade.dependents_covered.length > 0
           );
  }
  
  return false;
};

function BenefitSetupDetail({ gmcGradeCodes, selectedBenefit, onProductCompletion, completedProducts, totalProducts }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialValues, setInitialValues] = useState(INIT_FORMIK_VALUES);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInceptionDone, setIsInceptionDone] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const formikRef = useRef(null);
  const [choiceSelection, setChoiceSelection] = useState(null);

  const { saveBenefit, getPlanBenefitData } = useFetchWellness();
  // const isEditFromSummary = searchParams.get("returnTo") === "summary-post";
  const [currentBenefitData, setCurrentBenefitData] = useState(null)

  useEffect(() => {
    if (selectedBenefit) {
      (async function () {
        const { data } = await http.get(
          "/benefits/get_vendor_benefit_mapping/",
          {
            params: {
              benefit_id: selectedBenefit,
            },
          },
        );
        const vendors = data.map((ven) => ({
          value: ven?.vendor?.id,
          label: ven?.vendor?.name,
        }));
        setVendorOptions(vendors);
      })();
    }
  }, [selectedBenefit]);

  const fetchBenefitDetail = async (benefit_id) => {
    if (!benefit_id) return;
    const { data } = await http.get("/business/get_plan_benefit_data/", {
      params: {
        benefit_id,
      },
    });
    return data;
  };

  const fetchBenefitData = async () => {
    try {
      const benefitId =
        new URLSearchParams(window.location.search).get("benefit") || null;
      const benefitData = await fetchBenefitDetail(benefitId);
      setCurrentBenefitData(benefitData);
    } catch (error) {
      console.error("Error fetching benefit:", error);
    }
  };

  useEffect(() => {
    fetchBenefitData();
  }, []);

  const cleanBenefitPayload = (values) => {
    if(values.choiceType === "SAME") {
        values.grades = []
    } else {
      values.benefit_description = ""
      values.benefit_details = []
      values.benefit_heading = ""
      values.dependents_covered = []
      // values.entity_id = ""
      values.redeem_option_data = ""
    }
    return values
  }

  const benefitDataHandler = (selectionChoice) => {
    if (currentBenefitData && currentBenefitData.length > 0) {
      const benefitData = currentBenefitData[0];
      if (currentBenefitData.length === 1) {
        setInitialValues({
          choiceType: selectionChoice === null ? "SAME" : selectionChoice,
          entity_id: benefitData?.entity_id || "",
          benefit_details: benefitData?.benefit_details || [],
          vendor_id: benefitData?.vendor || "",
          benefit_heading: benefitData?.benefit_heading || "",
          benefit_description: benefitData?.benefit_description || "",
          redeem_option_data: benefitData?.redeem_option_data || "",
          dependents_covered: benefitData?.dependents_covered || [],
          grades: [],
        });
      } else if (currentBenefitData.length > 1) {
        setInitialValues({
          choiceType: selectionChoice === null ? "GRADED" : selectionChoice,
          entity_id: "",
          benefit_details: [],
          vendor_id: currentBenefitData?.[0]?.vendor || "",
          benefit_heading: "",
          redeem_option_data: "",
          benefit_description: "",
          dependents_covered: [],
          grades: currentBenefitData.map((benef) => ({
            dependents_covered: benef?.dependents_covered || [],
            benefit_description: benef?.benefit_description || "",
            redeem_option_data: benef?.redeem_option_data || "",
            benefit_heading: benef?.benefit_heading || "",
            benefit_details: benef?.benefit_details || [""],
            entity_id: benef?.entity_id || [],
          })),
        });
      }
    } else {
      //console.log("# check 3")
    }
  }

  const fetchChoiceLevelData = async (selectionChoice) => {
    await fetchBenefitData()
    await benefitDataHandler(selectionChoice)
  }

  useLayoutEffect(() => {
    benefitDataHandler(null)
  }, [currentBenefitData, choiceSelection]);

  const navigateToSummary = (formikProps) => {
    const returnTo = searchParams.get("returnTo");
    const returnBenefitId = searchParams.get("benefitId");
    const returnName = searchParams.get("name");

    if (returnTo === "summary-post" && returnBenefitId) {
      const basePath = isOldFlow
        ? `/wellness/setup/summary-post/${returnBenefitId}`
        : `/v1/wellness/setup/summary-post/${returnBenefitId}`;
      const query = new URLSearchParams();
      if (returnName) query.set("name", returnName);
      navigate(`${basePath}?${query.toString()}`, { replace: true });
      return;
    }

    // Check if all products are completed OR if current form has valid data
    const allProductsCompleted = completedProducts && completedProducts.size >= totalProducts;
    const hasValidFormData = formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values));
    
    if (!allProductsCompleted && !hasValidFormData) {
      // Show message that all products must be completed first OR form must have valid data
      alert(`Please complete all ${totalProducts} product setups or fill in all required fields before proceeding to summary.`);
      return;
    }
    
    // Navigate to summary
    if (isOldFlow) {
      navigate("/wellness/setup/summary", { replace: true });
    } else {
      navigate("/v1/wellness/setup/summary", { replace: true });
    }
  };

  const isOldFlow = useMemo(() => {
    return !location?.pathname.includes("v1");
  }, [location]);

  const submitHandler = async (values) => {
    try {
      setIsSaving(true);
      cleanBenefitPayload(values)
      await saveBenefit({
        ...values,
        benefit_id: selectedBenefit,
        gmcGradeCodes,
      });
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Mark product as completed
      if (onProductCompletion) {
        onProductCompletion(selectedBenefit);
      }
      
    } catch (error) {
      // Handle error appropriately
      console.error("Error saving benefit:", error);
      // You might want to show an error message to the user
    } finally {
      setIsSaving(false);
    }
  };

  // Removed areAllRequiredFieldsFilled function as it's no longer used
  const resetCurrentFormikValues = () => {
    const clearedValues = getEmptyFormValues(
      formikRef.current?.values?.choiceType || "SAME"
    );
    setCurrentBenefitData(null);
    setInitialValues(clearedValues);
    setFormResetKey((prev) => prev + 1);
    if (formikRef.current) {
      formikRef.current.resetForm({
        values: clearedValues,
      });
    }
  };

  useEffect(() => {
    resetCurrentFormikValues();
    fetchBenefitData()
  }, [selectedBenefit])

  const isInceptionAlreadyDone = () => {
    if(currentBenefitData) {
      return currentBenefitData[0]?.is_inception_done
    }
  }

  return (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message="Benefit Saved Successfully!"
      />
      <Formik key={`${selectedBenefit || "no-benefit"}-${formResetKey}`} innerRef={formikRef} enableReinitialize={true} initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitHandler}>
      {(formikProps) => {
        // Removed disabledButton as it's no longer used for submit button

        return (
          <Form>
            <BenefitDetailWrapper>
              <BenefitFormWrapper>
                {gmcGradeCodes?.length > 0 && (
                  <BenefitSetupDetailSection title="Choose Type" className="choose-type-section">
                    <ChooseTypeWrapper>
                      {CHOICE_TYPE.map(({ display_name, value }) => (
                        <Field name="choiceType" key={value}>
                          {({ field, form }) => (
                            <DependentsCard
                              disabled={isInceptionAlreadyDone()}
                              handleClick={() => {
                                if (!isSaving && !isInceptionDone) {
                                  form.resetForm({
                                    values: {
                                      ...formikProps.values,
                                      grades: [],
                                    },
                                  });
                                  form.setFieldValue("choiceType", value);
                                  // fetchSelectionBasedData(value)
                                  // setChoiceSelection(value)
                                  fetchChoiceLevelData(value)
                                  
                                  // Save the choice type preference for this benefit
                                  localStorage.setItem(`benefit_choice_type_${selectedBenefit}`, value);
                                }
                              }}
                              selected={formikProps.values.choiceType === value}
                              className="dependents-card"
                              style={{
                                opacity: isSaving || isInceptionDone ? 0.6 : 1,
                                pointerEvents: isSaving || isInceptionDone ? "none" : "auto",
                              }}
                            >
                              <Ptag>{display_name}</Ptag>
                            </DependentsCard>
                          )}
                        </Field>
                      ))}
                    </ChooseTypeWrapper>
                  </BenefitSetupDetailSection>
                )}
                <BenefitSetupDetailSection title="Select Vendor" className="select-vendor-section">
                  <BaseDropdown
                    fontSize="14"
                    options={vendorOptions}
                    formikProps={{ ...formikProps, name: "vendor_id" }}
                    disabled={isSaving}
                  />
                </BenefitSetupDetailSection>
                <BenefitSetupDetailGrade
                  formikProps={formikProps}
                  gmcGradeCodes={gmcGradeCodes}
                  disabled={isSaving}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <BenefitSaveBtn 
                    label={isSaving ? "Saving..." : "Save"} 
                    type="submit" 
                    disabled={isSaving}
                    style={{
                      opacity: isSaving ? 0.8 : 1,
                      pointerEvents: isSaving ? "none" : "auto",
                      position: 'relative'
                    }}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                  />
                </Box>
              </BenefitFormWrapper>
            </BenefitDetailWrapper>

            <LargeButton
              label="Submit"
              type="button"
              onClick={() => navigateToSummary(formikProps)}
              disabled={!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))}
              style={{
                opacity: (!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))) ? 0.6 : 1,
                pointerEvents: (!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))) ? "none" : "auto",
                marginTop: "1.5rem",
              }}
            />
         
          </Form>
        );
      }}
    </Formik>
    </>
  );
}

export default BenefitSetupDetail;
